import { Tooltip } from 'antd';
import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Link, Routes, Route } from 'react-router-dom';
import { Layout, Menu } from 'antd';
import equal from 'fast-deep-equal';
import FilesGroup from '../FilesGroup/FilesGroup';
import SearchProfile from '../SearchProfile/SearchProfile';
import Loading from '../Loading/Loading';
import { lang, locale } from '../../api/config';

const { Header, Content, Sider } = Layout;

// 样式常量
const STYLES = {
  header: {
    padding: 0,
    background: '#fff',
    width: '84.25%',
    position: 'absolute',
    transform: 'translate(10%,-50%)',
    margin: 'auto',
  },
  menu: {
    flex: 1,
    justifyContent: 'center',
    fontSize: 16,
    fontWeight: 500,
  },
  sider: {
    background: '#F9F9F9',
    borderLeft: '1px solid #ECECEC',
    borderRight: '1px solid #ECECEC',
  },
  siderMenu: {
    height: '100%',
    borderRight: 0,
    background: '#F9F9F9',
    whiteSpace: 'nowrap',
  },
  content: {
    padding: 24,
    margin: 0,
    minHeight: 280,
  },
  menuLabel: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'inline-block',
    verticalAlign: 'middle',
    maxWidth: '100%',
  },
};

// 自定义组件 EllipsisTooltip，用于溢出时显示 Tooltip
const EllipsisTooltip = ({ title, children }) => {
  const textRef = useRef(null);
  const [isOverflowed, setIsOverflowed] = useState(false);

  useEffect(() => {
    const element = textRef.current;
    if (element) {
      // 检查是否存在溢出
      setIsOverflowed(element.scrollWidth > element.clientWidth);
    }
  }, [title, children]);

  return (
    <Tooltip title={isOverflowed ? title : ''}>
      <span
        ref={textRef}
        style={STYLES.menuLabel}
      >
        {children}
      </span>
    </Tooltip>
  );
};

const LayoutCenter = ({
  flag,
  onChangeFlag,
  keyword,
  selectedIndustry,
  onChangeSelectedIndustry,
  industries,
  typeSeries,
  onChangeTypeSeries,
  selectedSeries,
  onchangeSelectedSeries,
  openType,
  onchangeOpenType,
  loading,
  onChangeLoading,
  handleRouter,
}) => {
  console.log('openType in TypeSeries:', openType);
  localStorage.setItem('openType', openType);

  // 创建行业菜单
  const menuIndustries = useMemo(
    () =>
      industries.map(industry => ({
        key: industry.id,
        label: (
          <Link to={`/${industry.id}`}>
            <strong>{industry.name[locale].toUpperCase()}</strong>
          </Link>
        ),
      })),
    [industries]
  );

  // 创建产品系列菜单
  const menuTypeSeries = useMemo(
    () =>
      typeSeries.length > 0
        ? typeSeries
            .map(type => {
              if (type.name) {
                return {
                  key: type.id,
                  label: (
                    <EllipsisTooltip title={type.name}>
                      {type.name}
                    </EllipsisTooltip>
                  ),
                  children:
                    type.product_series.length > 0
                      ? type.product_series.map(series => ({
                          key: series.id,
                          label: (
                            <Link to={`${type.id}/${series.id}`}>
                              {series.name}
                            </Link>
                          ),
                        }))
                      : [],
                };
              }
              return null;
            })
            .filter(Boolean)
        : [],
    [typeSeries]
  );

  // 设置展开的菜单项
  const openKeys = useMemo(
    () =>
      openType ? [openType] : typeSeries.length > 0 ? [typeSeries[0].id] : [],
    [openType, typeSeries]
  );

  // 设置选中的系列菜单项
  const selectedSeriesKeys = useMemo(
    () =>
      selectedSeries
        ? [selectedSeries]
        : typeSeries.length > 0 && typeSeries[0].product_series.length > 0
        ? [typeSeries[0].product_series[0].id]
        : [],
    [selectedSeries, typeSeries]
  );

  // 获取数据
  const data = useMemo(() => {
    if (!selectedSeries) {
      return typeSeries.length > 0 && typeSeries[0].product_series.length > 0
        ? typeSeries[0].product_series[0]
        : {};
    }
    if (openType && typeSeries.length > 0) {
      const typeData = typeSeries.find(type => type.id === openType) || {};
      return (
        typeData.product_series?.find(series => series.id === selectedSeries) ||
        {}
      );
    }
    return {};
  }, [selectedSeries, typeSeries, openType]);

  // 处理菜单选择事件
  const handleSelect = key => {
    const delay = lang === 'cn' ? 300 : 1000;

    const timeoutId = setTimeout(() => {
      onChangeSelectedIndustry(key);
      onChangeLoading(true);
      if (lang === 'cn') {
        onChangeTypeSeries([]);
      }
      onchangeOpenType(null);
      onchangeSelectedSeries(null);
      onChangeFlag(false);
    }, delay);

    return () => clearTimeout(timeoutId);
  };

  return (
    <Layout>
      <Header style={STYLES.header}>
        <Menu
          theme='light'
          mode='horizontal'
          selectedKeys={[selectedIndustry]}
          items={menuIndustries}
          onSelect={({ key }) => handleSelect(key)}
          style={STYLES.menu}
        />
      </Header>
      {loading ? (
        <Loading />
      ) : !flag ? (
        <Routes>
          {industries.map(industry => (
            <Route
              key={industry.id}
              path={industry.id}
              element={
                <LevelRouterContainer
                  selectedSeriesKeys={selectedSeriesKeys}
                  openKeys={openKeys}
                  menuTypeSeries={menuTypeSeries}
                  onchangeSelectedSeries={onchangeSelectedSeries}
                  onchangeOpenType={onchangeOpenType}
                  typeSeries={typeSeries}
                  data={data}
                />
              }
            >
              {typeSeries.length > 0 && handleRouter()}
            </Route>
          ))}
        </Routes>
      ) : (
        <Layout>
          <Content style={STYLES.content}>
            <SearchProfile keyword={keyword} />
          </Content>
        </Layout>
      )}
    </Layout>
  );
};

// 子组件：LevelRouterContainer
const LevelRouterContainer = ({
  selectedSeriesKeys,
  openKeys,
  menuTypeSeries,
  onchangeSelectedSeries,
  onchangeOpenType,
  typeSeries,
  data,
}) => {
  const handleRouterTwo = () => {
    if (!typeSeries.length) return [];

    return typeSeries.flatMap(type =>
      type.product_series.map(series => (
        <Route
          key={`${type.id}-${series.id}`}
          path={`${type.id}/${series.id}`}
          element={<LevelRouterContent data={data} />}
        />
      ))
    );
  };

  return (
    <Layout style={{ paddingLeft: '8.25%' }}>
      <Sider
        width='16.667vw'
        style={STYLES.sider}
      >
        <Menu
          mode='inline'
          selectedKeys={selectedSeriesKeys}
          openKeys={openKeys}
          onOpenChange={openKeys => {
            onchangeOpenType(openKeys[1]);
          }}
          onClick={({ keyPath }) => {
            onchangeOpenType(keyPath[1]);
            onchangeSelectedSeries(keyPath[0]);
          }}
          style={STYLES.siderMenu}
          items={menuTypeSeries}
        />
      </Sider>
      <Routes>{typeSeries.length > 0 && handleRouterTwo()}</Routes>
    </Layout>
  );
};

// 子组件：LevelRouterContent
const LevelRouterContent = React.memo(({ data = {} }) => {
  return (
    <Layout>
      <Content style={STYLES.content}>
        {Object.keys(data).length > 0 && <FilesGroup data={data} />}
      </Content>
    </Layout>
  );
});

export default React.memo(LayoutCenter, equal);
